import React, { useCallback, useContext, useMemo } from "react";
import { useSelector } from "react-redux";

import { makeStyles } from "tss-react/mui";

import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Select from "@mui/material/Select";
import Paper from "@mui/material/Paper";

import { ButtonUI } from "../../../../../Common";

import { setPrevState } from "../../../../../../util/common";
import { regexName, regexSnowflakeField } from "../../../../../../util/regularExpress";
import { checkReservedKeywords } from "../../../../../../util/restrictedStatement";
import { DestinationContext } from "..";
import KMKGroupSelect from "../../../../../Common/KMKGroupSelect";

const salesforceOperations = [
  {
    text: "Insert",
    value: "insert",
  },
  {
    text: "Update",
    value: "update",
  },
  {
    text: "Delete",
    value: "delete",
  },
  {
    text: "Upsert",
    value: "upsert",
  },
];

const CustomPaper = React.memo((props) => {
  return (
    <Paper
      {...props}
      sx={{
        "& .MuiAutocomplete-listbox": {
          maxWidth: "100%",
          wordBreak: "break-all !important",
        },
      }}
    />
  );
});

const useStyles = makeStyles()((theme) => ({
  MenuItem: {
    whiteSpace: "normal",
    wordWrap: "break-word",
    wordBreak: "break-all",
  },
}));

const SalesforceProperties = ({
  exchangeJobState,
  setExchangeJobState,
  previousState,
  destinationType = "salesforce",
}) => {
  const { classes } = useStyles();
  const { schemaName, tableList, tableName, externalIdFieldName } = exchangeJobState;

  return (
    <Grid container spacing={1}>
      <Grid>
        <FormControl required variant="outlined" sx={{ width: { xs: "100%", sm: "300px" }, m: 1, ml: 0 }}>
          <InputLabel>Object API Name</InputLabel>
          <Select
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 200,
                  maxWidth: 300,
                },
              },
            }}
            label="Object API Name"
            value={tableName}
            onChange={(e) => {
              setPrevState(setExchangeJobState, {
                tableName: e.target.value,
                isFieldsConfigured:
                  e.target.value !== previousState.tableName ? false : exchangeJobState.isFieldsConfigured,
              });
            }}
          >
            {tableList.map((table) => (
              <MenuItem className={classes.MenuItem} key={table} value={table}>
                {table}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid>
        <FormControl required variant="outlined" sx={{ width: { xs: "100%", sm: "300px" }, m: 1, ml: 0 }}>
          <InputLabel>Operation type</InputLabel>
          <Select
            label="Operation type"
            value={schemaName}
            onChange={(e) => {
              setPrevState(setExchangeJobState, {
                schemaName: e.target.value,
              });
            }}
          >
            {salesforceOperations.map(({ text, value }) => (
              <MenuItem key={value} value={value}>
                {text}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      {(["update", "upsert"].includes(schemaName) || (destinationType === "veevavault" && schemaName === "delete")) && (
        <Grid>
          <FormControl required variant="outlined" sx={{ width: { xs: "100%", sm: "300px" }, m: 1, ml: 0 }}>
            <TextField
              value={externalIdFieldName}
              sx={{ width: { xs: "100%", sm: "300px" } }}
              required
              label="ID Field"
              inputProps={{ maxLength: 40 }}
              onChange={(e) => {
                const externalIdFieldName = e.target.value;
                const isValid = externalIdFieldName.length !== 0 ? regexSnowflakeField(externalIdFieldName) : true;
                if (isValid)
                  setPrevState(setExchangeJobState, {
                    externalIdFieldName: e.target.value,
                  });
              }}
            />
          </FormControl>
        </Grid>
      )}
    </Grid>
  );
};

const TableProperties = ({ exchangeJobState, setExchangeJobState, destinationType, previousState }) => {
  const { restrictedObject, loadSnowflakeTables, loadExternalSnowflakeTables, loadmysqlTables } =
    useContext(DestinationContext);
  const { delimiters, fileTypes } = useSelector((state) => state.config);

  const {
    sourceType,
    destination,
    schemaName,
    groupBy,
    snowflakeSchemas,
    externalSnowflakeSchemas,
    tableList,
    tableName,
    destinationAction,
    existingTableAction,
    tableNameError,
    showSchemaError,
    allSchemas,
    customDelimeter,
    customNullValue,
    delimiter,
    fileType,
    locationList,
    nullValue,
    source,
    sourcePath,
    sourcePathError,
  } = exchangeJobState;

  const handleOnChange = useCallback(
    async (e) => {
      setPrevState(setExchangeJobState, { loading: true });
      setPrevState(setExchangeJobState, {
        schemaName: e.target.value,
        tableName: "",
        showSchemaError: false,
        ...(destinationType === "mysql" ? { tableList: await loadmysqlTables(destination, e.target.value) } : {}),
        ...(destinationType === "snowflake"
          ? { tableList: await loadSnowflakeTables(e.target.value.toUpperCase()) }
          : {}),
        loading: false,
        isFieldsConfigured: e.target.value !== previousState.schemaName ? false : exchangeJobState.isFieldsConfigured,
      });
    },
    [
      destination,
      destinationType,
      exchangeJobState.isFieldsConfigured,
      loadSnowflakeTables,
      loadmysqlTables,
      previousState.schemaName,
      setExchangeJobState,
    ]
  );

  const handleOnChangeExternal = useCallback(
    async (e, value) => {
      const database = value?.groupBy?.toUpperCase() || "";
      const schema = value?.title?.toUpperCase() || "";

      setPrevState(setExchangeJobState, { loading: true });
      setPrevState(setExchangeJobState, {
        schemaName: schema,
        groupBy: database,
        tableName: "",
        showSchemaError: false,
        tableList: (await loadExternalSnowflakeTables(destination, database, schema)) || [],
        loading: false,
        isFieldsConfigured: value?.title !== previousState.schemaName ? false : exchangeJobState.isFieldsConfigured,
      });
    },
    [
      destination,
      exchangeJobState.isFieldsConfigured,
      loadExternalSnowflakeTables,
      previousState.schemaName,
      setExchangeJobState,
    ]
  );

  // Check condition to disabled Save Button
  const disableConfigureButton = useMemo(() => {
    let disabledSave = locationList.length && source && destination;

    if (["sftp", "azure", "s3", "sharepoint"].includes(sourceType.toLowerCase()))
      disabledSave = disabledSave && sourcePath?.trim() && !sourcePathError;

    disabledSave =
      disabledSave &&
      schemaName?.trim() &&
      tableName &&
      !tableNameError &&
      tableName.trim() &&
      fileTypes.length &&
      fileType &&
      delimiters.length &&
      (delimiter === "custom" ? customDelimeter : delimiter) &&
      (nullValue === "custom" ? customNullValue : nullValue);
    return !disabledSave;
  }, [
    delimiters.length,
    customDelimeter,
    customNullValue,
    delimiter,
    destination,
    fileType,
    locationList.length,
    nullValue,
    schemaName,
    source,
    sourcePath,
    sourcePathError,
    tableName,
    tableNameError,
    fileTypes.length,
    sourceType,
  ]);

  return (
    <>
      <RadioGroup
        value={destinationAction}
        onChange={(e) => {
          setPrevState(setExchangeJobState, {
            tableName: "",
            destinationAction: e.target.value,
            isFieldsConfigured:
              e.target.value !== previousState.destinationAction ? false : exchangeJobState.isFieldsConfigured,
          });
        }}
      >
        <Grid container spacing={1}>
          <Grid size={12}>
            <Grid container spacing={3}>
              <Grid
                alignSelf={"center"}
                size={{
                  xs: 12,
                  sm: 4,
                  md: 2
                }}>
                Schema
              </Grid>
              <Grid
                sx={{ display: "flex" }}
                size={{
                  xs: 12,
                  sm: 8,
                  md: 4
                }}>
                <FormControl
                  variant="outlined"
                  error={showSchemaError}
                  sx={{ width: { xs: "100%", sm: "300px" }, m: 1, ml: 0 }}
                >
                  {destination === "SNOWFLAKE" || destinationType !== "snowflake" ? (
                    <>
                      <Select value={schemaName || ""} onChange={handleOnChange}>
                        {restrictedObject.schema === schemaName && (
                          <MenuItem
                            sx={{
                              display: "none",
                            }}
                            value={schemaName}
                          >
                            ********
                          </MenuItem>
                        )}
                        {(destinationType === "snowflake" ? snowflakeSchemas : allSchemas)?.map((schema) => (
                          <MenuItem key={schema} value={schema}>
                            {schema?.toUpperCase()}
                          </MenuItem>
                        ))}
                      </Select>
                      {showSchemaError && <FormHelperText>Previously selected schema no longer exists</FormHelperText>}
                    </>
                  ) : (
                    <KMKGroupSelect
                      options={externalSnowflakeSchemas}
                      label="Schema"
                      value={{ title: schemaName, groupBy }}
                      handleChange={handleOnChangeExternal}
                      error={showSchemaError}
                      errorText={"Previously selected schema no longer exists"}
                      noOptionsText={"No schema found"}
                      PaperComponent={CustomPaper}
                      fullWidth
                      required
                    />
                  )}
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
          <Grid size={12}>
            <Grid container spacing={3}>
              <Grid
                size={{
                  xs: 12,
                  sm: 4,
                  md: 2
                }}>
                <FormControlLabel
                  value="new"
                  control={<Radio disabled={restrictedObject.schema === schemaName && schemaName !== ""} />}
                  label="Create New"
                />
              </Grid>
              <Grid
                sx={{ display: "flex" }}
                size={{
                  xs: 12,
                  sm: 8,
                  md: 4
                }}>
                <TextField
                  required
                  value={
                    destinationAction === "new"
                      ? restrictedObject.schema === schemaName && schemaName !== ""
                        ? "********"
                        : tableName
                      : ""
                  }
                  label="Table Name"
                  variant="outlined"
                  inputProps={{ maxLength: 50 }}
                  disabled={destinationAction !== "new" || restrictedObject.schema === schemaName || schemaName === ""}
                  onChange={(e) => {
                    let errorMessage = "";
                    const isTableExist = tableList.some(
                      (table) => table.toLowerCase() === e.target.value.toLowerCase()
                    );

                    if (checkReservedKeywords(e.target.value)) errorMessage = "Reserved keyword.";
                    else if (isTableExist) errorMessage = "Table already exists";
                    const isNameValid = regexName(e.target.value, "alphaWithUnderscore");
                    setPrevState(setExchangeJobState, {
                      tableNameError: errorMessage,
                      tableName: isNameValid ? e.target.value : exchangeJobState.tableName,
                      isFieldsConfigured:
                        isNameValid && e.target.value !== previousState.tableName
                          ? false
                          : exchangeJobState.isFieldsConfigured,
                    });
                  }}
                  error={destinationAction === "new" && !!tableName && !!tableNameError}
                  helperText={destinationAction === "new" && !!tableName && tableNameError}
                  sx={{ width: { xs: "100%", sm: "300px" }, m: 1, ml: 0 }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid size={12}>
            <Grid container spacing={3} sx={{ display: "flex", alignItems: "center" }}>
              <Grid
                size={{
                  xs: 12,
                  sm: 4,
                  md: 2
                }}>
                <FormControlLabel
                  value="existing"
                  control={<Radio disabled={restrictedObject.schema === schemaName && schemaName !== ""} />}
                  label="Use Existing"
                />
              </Grid>
              <Grid
                sx={{ display: "flex" }}
                size={{
                  xs: 12,
                  sm: 8,
                  md: 4
                }}>
                {restrictedObject.schema === schemaName && schemaName !== "" && destinationAction === "existing" ? (
                  <FormControl
                    required
                    variant="outlined"
                    disabled={
                      destinationAction !== "existing" || restrictedObject.schema === schemaName || schemaName === ""
                    }
                    sx={{ width: { xs: "100%", sm: "300px" }, m: 1, ml: 0 }}
                  >
                    <TextField label="Table" value="********" disabled />
                  </FormControl>
                ) : (
                  <FormControl
                    required
                    variant="outlined"
                    disabled={destinationAction !== "existing" || restrictedObject.schema === schemaName}
                    sx={{ width: { xs: "100%", sm: "300px" }, m: 1, ml: 0 }}
                  >
                    <InputLabel>Table</InputLabel>
                    <Select
                      label="Table"
                      value={
                        destinationAction === "existing"
                          ? destinationType === "snowflake"
                            ? tableName.toUpperCase()
                            : tableName
                          : ""
                      }
                      onChange={(e) => {
                        setPrevState(setExchangeJobState, {
                          tableName: e.target.value,
                          isFieldsConfigured:
                            e.target.value !== previousState.tableName ? false : exchangeJobState.isFieldsConfigured,
                        });
                      }}
                    >
                      {destinationAction === "existing" &&
                        tableList.map((table) => (
                          <MenuItem key={table} value={table}>
                            {table}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                )}
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 12,
                  md: 6
                }}>
                <RadioGroup
                  row
                  value={existingTableAction}
                  onChange={(e) =>
                    setPrevState(setExchangeJobState, {
                      existingTableAction: e.target.value,
                      isFieldsConfigured:
                        e.target.value !== previousState.existingTableAction
                          ? false
                          : exchangeJobState.isFieldsConfigured,
                    })
                  }
                >
                  <FormControlLabel
                    disabled={destinationAction !== "existing"}
                    value="overwrite"
                    control={<Radio />}
                    label="Overwrite data"
                  />
                  <FormControlLabel
                    disabled={destinationAction !== "existing"}
                    value="append"
                    control={<Radio />}
                    label="Append data"
                  />
                </RadioGroup>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </RadioGroup>
      <Grid size={12}>
        <Box display="flex">
          <ButtonUI
            color="success"
            component="span"
            variant="contained"
            sx={{
              alignSelf: "right",
            }}
            disabled={disableConfigureButton}
            onClick={() => {
              setPrevState(setExchangeJobState, { mapping: true });
            }}
          >
            Configure Table
          </ButtonUI>
        </Box>
      </Grid>
    </>
  );
};

const TableAsTarget = ({ exchangeJobState, setExchangeJobState, destinationType, previousState }) => (
  <Grid size={12}>
    {["salesforce", "veevavault"].includes(destinationType) ? (
      <SalesforceProperties
        exchangeJobState={exchangeJobState}
        setExchangeJobState={setExchangeJobState}
        previousState={previousState}
        destinationType={destinationType}
      />
    ) : (
      <TableProperties
        exchangeJobState={exchangeJobState}
        setExchangeJobState={setExchangeJobState}
        destinationType={destinationType}
        previousState={previousState}
      />
    )}
  </Grid>
);

export default TableAsTarget;
